import { Box, Typography, Popover, useTheme } from "@mui/material";
import { formatCurrency } from "Utils/CurrencyUtils";
import type { CurrencyCode } from "Infrastructure/Api/Api";
import SavingsIcon from "@mui/icons-material/Savings";
import { useRef, useState } from "react";
import { RewardsSummary } from "Components/Loyalty/RewardsSummary/RewardsSummary";
import { Resources, useResource } from "Translations/Resources";

type Props = {
  totalRewards?: number;
  currencyCode?: CurrencyCode;
  totalPoints?: number;
  totalPointsLabel: string;
  myShare?: number;
  colorVariant?: "light" | "dark";
};

export const RewardsWithPopup = ({
  totalRewards = 0,
  currencyCode,
  totalPoints = 0,
  totalPointsLabel,
  myShare = 0,
  colorVariant = "light",
}: Props) => {
  const { t } = useResource();
  const [isOpen, setIsOpen] = useState(false);
  const rewardsAmountRef = useRef<HTMLButtonElement>(null);
  const theme = useTheme();

  const formattedTotalRewards = formatCurrency({
    amount: totalRewards,
    currencyCode,
    maxDecimalPlaces: 0,
    minDecimalPlaces: 0,
  });

  const formattedMyShare = formatCurrency({
    amount: myShare,
    currencyCode,
    maxDecimalPlaces: 2,
    minDecimalPlaces: 2,
  });

  const bgColor = theme.palette.headerWidget[colorVariant].background;
  const color = theme.palette.headerWidget[colorVariant].text;

  return (
    <>
      <Box
        display="flex"
        p={3}
        gap={2}
        alignItems="center"
        bgcolor={bgColor}
        borderRadius={10}
        height={40}
        sx={{ cursor: "pointer", border: "none" }}
        onClick={() => setIsOpen(!isOpen)}
        ref={rewardsAmountRef}
        component="button"
        type="button"
        aria-label={t(Resources.UserMenu.Rewards.OpenRewardsDetails)}
      >
        <SavingsIcon sx={{ fontSize: 20, color }} />
        <Typography color={color} fontSize={14} fontWeight={600}>
          {formattedTotalRewards}
        </Typography>
      </Box>
      <Popover
        open={isOpen}
        anchorEl={rewardsAmountRef.current}
        onClose={() => setIsOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { mt: 3, minWidth: 280 } } }}
      >
        <RewardsSummary
          totalRewards={formattedTotalRewards}
          totalPoints={totalPoints}
          totalPointsLabel={totalPointsLabel}
          myShare={formattedMyShare}
        />
      </Popover>
    </>
  );
};
